<template>
  <div class="invoices-status-widget-container">
    <div v-if="loading" class="invoices-status-widget-loading">Loading...</div>
    <div v-else-if="error" class="invoices-status-widget-error">{{ error }}</div>
    <div v-else class="invoices-status-widget-content">
      <div class="invoices-status-widget-header">
        <div class="invoices-status-widget-header-label">Invoices Status</div>
      </div>
      <div class="invoices-status-widget-details">
        <div class="invoices-status-widget-detail-row">
          <div class="invoices-status-widget-detail-label">
            <span class="invoices-status-widget-badge invoices-status-widget-open"></span>
            Open Invoices
          </div>
          <div class="invoices-status-widget-detail-value">
            {{ openInvoicesCount }}
          </div>
        </div>
        <div class="invoices-status-widget-detail-row">
          <div class="invoices-status-widget-detail-label">
            <span class="invoices-status-widget-badge invoices-status-widget-overdue"></span>
            Overdue Invoices
          </div>
          <div class="invoices-status-widget-detail-value invoices-status-widget-overdue-value">
            {{ overdueInvoicesCount }}
          </div>
        </div>
        <div class="invoices-status-widget-detail-row">
          <div class="invoices-status-widget-detail-label">Total Amount Due</div>
          <div class="invoices-status-widget-detail-value invoices-status-widget-amount">{{ formatCurrency(totalAmountDue) }}</div>
        </div>
      </div>
      <div v-if="openInvoicesCount > 0 || overdueInvoicesCount > 0" class="invoices-status-widget-footer">
        <router-link :to="{ path: `/company/${companyId}/dashboard/customers/invoices` }" class="invoices-status-widget-link">View all invoices →</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import './styles/InvoicesStatusWidget.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';

export default {
  name: 'InvoicesStatusWidget',
  props: {
    companyId: { type: [String, Number], required: true }
  },
  data() {
    return {
      openInvoicesCount: 0,
      overdueInvoicesCount: 0,
      totalAmountDue: 0,
      loading: true,
      error: null
    };
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        const invoices = await ApiService.getAllCustomerInvoices(this.companyId);
        const invoicesArray = Array.isArray(invoices) ? invoices : [];
        
        // Filter out draft, cancelled, and fully paid invoices
        // Keep invoices that have a balance due (not fully paid)
        const validInvoices = invoicesArray.filter(inv => {
          const status = (inv.status || '').toLowerCase();
          const balance = parseFloat(inv.balanceAmount || inv.balanceDue || 0);
          
          // Exclude draft, cancelled, and fully paid invoices
          if (status === 'draft' || status === 'cancelled' || status === 'paid') {
            return false;
          }
          
          // Only include invoices with a balance due
          return balance > 0;
        });
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Count open invoices (sent or partially_paid with balance > 0, not overdue)
        this.openInvoicesCount = validInvoices.filter(inv => {
          const status = (inv.status || '').toLowerCase();
          const dueDate = inv.dueDate ? new Date(inv.dueDate) : null;
          
          // Skip if already marked as overdue
          if (status === 'overdue') {
            return false;
          }
          
          // Open invoices are: sent or partially_paid that are not overdue
          if (status === 'sent' || status === 'partially_paid') {
            // Check if overdue by dueDate
            if (dueDate) {
              dueDate.setHours(0, 0, 0, 0);
              return dueDate >= today; // Not overdue yet
            }
            return true; // No due date, consider open
          }
          return false;
        }).length;
        
        // Count overdue invoices (status = 'overdue' OR past dueDate)
        this.overdueInvoicesCount = validInvoices.filter(inv => {
          const status = (inv.status || '').toLowerCase();
          const dueDate = inv.dueDate ? new Date(inv.dueDate) : null;
          
          // Explicitly marked as overdue
          if (status === 'overdue') {
            return true;
          }
          
          // Check if past due date (for sent or partially_paid invoices)
          if ((status === 'sent' || status === 'partially_paid') && dueDate) {
            dueDate.setHours(0, 0, 0, 0);
            return dueDate < today;
          }
          
          return false;
        }).length;
        
        // Calculate total amount due (all valid invoices with balance)
        this.totalAmountDue = validInvoices.reduce((sum, inv) => {
          return sum + parseFloat(inv.balanceAmount || inv.balanceDue || 0);
        }, 0);
      } catch (error) {
        logger.error('Error fetching invoices status:', error);
        this.error = 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }
  }
};
</script>

